import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SubscriptionService } from '../services/SubscriptionService';
import { normalizePlanTier } from '../utils/planTierUtils';
import { resolveExpandedAccessState } from '../utils/expandedAccess';
import { useAuthContext } from './AuthProvider';

const SubscriptionContext = createContext(null);

function normalizeSubscription(rawSubscription) {
	if (!rawSubscription) return null;
	return {
		...rawSubscription,
		planName: normalizePlanTier(rawSubscription.planName),
		scheduledDowngradePlanName: normalizePlanTier(rawSubscription.scheduledDowngradePlanName)
	};
}

const MAX_FETCH_RETRIES = 5;
const BASE_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 30000;

export const SubscriptionProvider = ({ children }) => {
	// Must use AuthProvider context — a bare useAuth() is a separate state instance
	// that never sees login(), so the plan stays essentials until a full page refresh.
	const { isAuthenticated, isLoading: authLoading, user } = useAuthContext();
	const [subscription, setSubscription] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	// True once we have a definitive answer from the backend (a subscription or a
	// confirmed 404). While false, the plan is unknown — do NOT treat it as essentials
	// for irreversible UX (upsell modals etc.).
	const [isPlanVerified, setIsPlanVerified] = useState(false);
	const lastAuthUserRef = useRef(null);
	const retryCountRef = useRef(0);
	const retryTimerRef = useRef(null);

	const clearRetryTimer = useCallback(() => {
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
			retryTimerRef.current = null;
		}
	}, []);

	const fetchSubscription = useCallback(async ({ silent = false } = {}) => {
		if (!isAuthenticated) {
			setSubscription(null);
			setIsLoading(false);
			return null;
		}

		try {
			// Don't flip the page back to a full loading skeleton when we already
			// have a verified plan (e.g. background refresh / remount).
			if (!silent) {
				setIsLoading(true);
			}
			const response = await SubscriptionService.getCurrentSubscription();

			if (response.status === 200) {
				const next = normalizeSubscription(response.subscription || null);
				clearRetryTimer();
				retryCountRef.current = 0;
				setError(null);
				setSubscription(next);
				setIsPlanVerified(true);
				return next;
			}

			// Non-200 payload from a resolved request: treat as a transient failure,
			// keep whatever subscription state we already have and retry.
			throw new Error(response.message || `Unexpected status ${response.status}`);
		} catch (err) {
			// 404 means the user genuinely has no subscription — that's a real answer.
			if (err.response?.status === 404) {
				clearRetryTimer();
				retryCountRef.current = 0;
				setError(null);
				setSubscription(null);
				setIsPlanVerified(true);
				return null;
			}

			// 500 / network error: the plan is UNKNOWN, not "essentials".
			// Keep the previous subscription state and retry with backoff.
			console.error('Error fetching subscription (will retry):', err);
			setError(err.response?.data?.message || err.message || 'Failed to fetch subscription');

			if (retryCountRef.current < MAX_FETCH_RETRIES && !retryTimerRef.current) {
				const delay = Math.min(
					BASE_RETRY_DELAY_MS * 2 ** retryCountRef.current,
					MAX_RETRY_DELAY_MS
				);
				retryCountRef.current += 1;
				retryTimerRef.current = setTimeout(() => {
					retryTimerRef.current = null;
					fetchSubscription({ silent: true });
				}, delay);
			}
			return null;
		} finally {
			if (!silent) {
				setIsLoading(false);
			}
		}
	}, [isAuthenticated, clearRetryTimer]);

	useEffect(() => {
		// Wait for AuthProvider to finish reading the token before deciding anything.
		if (authLoading) {
			setIsLoading(true);
			return;
		}

		if (!isAuthenticated) {
			clearRetryTimer();
			retryCountRef.current = 0;
			lastAuthUserRef.current = null;
			setSubscription(null);
			setError(null);
			setIsPlanVerified(false);
			setIsLoading(false);
			return;
		}

		// Fetch on every authenticated session start (login or refresh).
		// lastAuthUserRef gates against Strict Mode double-invoke / dep churn.
		if (lastAuthUserRef.current === 'authenticated') {
			return;
		}
		lastAuthUserRef.current = 'authenticated';
		clearRetryTimer();
		retryCountRef.current = 0;
		fetchSubscription();
	}, [isAuthenticated, authLoading, fetchSubscription, clearRetryTimer]);

	useEffect(() => clearRetryTimer, [clearRetryTimer]);

	const hasActiveSubscription = useCallback(() => {
		if (!subscription) return false;
		// Match backend getActiveSubscription / getEffectivePlanName (active + trialing)
		return subscription.status === 'active' || subscription.status === 'trialing';
	}, [subscription]);

	const getCurrentPlanName = useCallback(() => subscription?.planName || null, [subscription]);

	const getEffectivePlanName = useCallback(() => {
		if (!subscription?.planName) return 'essentials';
		if (subscription.status !== 'active' && subscription.status !== 'trialing') {
			return 'essentials';
		}
		return normalizePlanTier(subscription.planName) || 'essentials';
	}, [subscription]);

	const getBillingInterval = useCallback(() => subscription?.billingInterval || null, [subscription]);

	const isCancelingAtPeriodEnd = useCallback(() => subscription?.cancelAtPeriodEnd || false, [subscription]);

	const getPeriodEndDate = useCallback(() => subscription?.currentPeriodEnd || null, [subscription]);

	const getScheduledDowngrade = useCallback(() => {
		if (!subscription?.scheduledDowngradePlanName || !subscription?.scheduledDowngradeEffectiveDate) {
			return null;
		}
		return {
			planName: subscription.scheduledDowngradePlanName,
			effectiveDate: subscription.scheduledDowngradeEffectiveDate
		};
	}, [subscription]);

	const getExpandedAccessState = useCallback(
		() => resolveExpandedAccessState(subscription, user?.createdAt),
		[subscription, user?.createdAt]
	);

	const refreshSubscription = useCallback(
		(options = {}) => {
			clearRetryTimer();
			retryCountRef.current = 0;
			// Default to silent when we already showed a plan — avoids Subscription page flicker.
			const silent = options.silent ?? isPlanVerified;
			return fetchSubscription({ silent });
		},
		[fetchSubscription, clearRetryTimer, isPlanVerified]
	);

	const confirmCheckoutSession = useCallback(
		async (sessionId) => {
			if (!sessionId) return fetchSubscription();
			try {
				setIsLoading(true);
				const response = await SubscriptionService.confirmCheckoutSession(sessionId);
				if (response.status === 200) {
					const next = normalizeSubscription(response.subscription || null);
					setSubscription(next);
					setIsPlanVerified(true);
					setError(null);
					return {
						...response,
						subscription: next,
						allowancesReset: !!response.allowancesReset,
						message: response.message
					};
				}
			} catch (err) {
				console.error('Error confirming checkout session:', err);
			} finally {
				setIsLoading(false);
			}
			return fetchSubscription();
		},
		[fetchSubscription]
	);

	const value = {
		subscription,
		isLoading,
		error,
		isPlanVerified,
		hasActiveSubscription,
		getCurrentPlanName,
		getEffectivePlanName,
		getBillingInterval,
		isCancelingAtPeriodEnd,
		getPeriodEndDate,
		getScheduledDowngrade,
		getExpandedAccessState,
		refreshSubscription,
		confirmCheckoutSession
	};

	return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
	const context = useContext(SubscriptionContext);
	if (!context) {
		throw new Error('useSubscription must be used within a SubscriptionProvider');
	}
	return context;
};
